import { osloDateString } from "./oslo-time";

/**
 * VIB-2093: hvilke arbeidsdager den ansatte mangler timeføring for.
 *
 * Ren logikk, ingen React og ingen Supabase, slik at hver regel kan testes for
 * seg. Reglene er hele poenget: en popup som roper om en helg, en feriedag
 * eller 17. mai blir klikket bort i refleks, og da fanger den heller ikke den
 * dagen som faktisk mangler.
 *
 * Speiler `supabase/functions/check-missing-hours/logic.ts` (VIB-1296), som
 * gjør den samme vurderingen for det daglige cron-varselet. De to kan ikke dele
 * kode — edge functions kjører i Deno og deler ikke modulgraf med `src/` — så
 * dette er en bevisst port. Ett bevisst avvik, se `isCoveredByAbsence`.
 */

const EXPECTED_DAILY_HOURS = 7.5;

/** Fraværsraden slik denne modulen trenger den (delmengde av `Absence`). */
export interface AbsenceCover {
  status: string;
  start_date: string;
  end_date: string;
  /** NULL = heldag. Tall = deldagsfravær, gjelder kun `start_date`. */
  hours: number | null;
}

/**
 * Legger `days` (kan være negativt) til en «YYYY-MM-DD»-streng.
 *
 * Bruker Date.UTC-komponentaritmetikk, ALDRI `new Date(dateStr)` på en
 * date-only-streng: den parses som UTC midnatt og leses tilbake lokalt, så i
 * norsk sommertid får du dagen før (VIB-1339).
 */
export function addDaysToDateString(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + days);
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(date.getUTCDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function isWeekendDateString(dateStr: string): boolean {
  const [y, m, d] = dateStr.split("-").map(Number);
  const day = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0=søn, 6=lør
  return day === 0 || day === 6;
}

/**
 * De `n` siste arbeidsdagene (man–fre minus `holidays`), bakover fra i går i
 * Oslo-tid. Hopper over hele helger og helligdagsrekker, så man finner alltid
 * nøyaktig `n` reelle arbeidsdager — en mandag ser tilbake på torsdag og
 * fredag forrige uke, ikke på lørdag og søndag.
 *
 * I dag telles ikke med: arbeidsdagen er ikke over, og å mase om timer for en
 * dag folk står midt oppe i er nettopp det som gjør en popup til støy.
 */
export function getLastNWorkdays(
  now: Date,
  n: number,
  holidays: ReadonlySet<string>,
): string[] {
  const workdays: string[] = [];
  let cursor = addDaysToDateString(osloDateString(now), -1);
  // Vernet mot uendelig løkke hvis holidays skulle komme inn feil; >30
  // sammenhengende ikke-arbeidsdager finnes ikke i en norsk kalender.
  let guard = 0;
  while (workdays.length < n && guard < 60) {
    if (!isWeekendDateString(cursor) && !holidays.has(cursor)) {
      workdays.push(cursor);
    }
    cursor = addDaysToDateString(cursor, -1);
    guard++;
  }
  return workdays.reverse(); // kronologisk, eldst først
}

/**
 * Er dagen dekket av fravær som gjør at vi ikke forventer timer?
 *
 * Avvik fra cron-en (VIB-1296), bevisst: den krever `status = 'approved'` og
 * type sykdom/ferie. Her teller alt som ikke er `rejected`, uansett type.
 * Grunnen er at KPI-fasiten allerede regner alle ikke-avviste fravær som
 * kapasitetsreduserende (legacy-rader står fortsatt som `pending`), og at
 * avspasering er like god grunn til å ikke ha ført timer som ferie. En popup
 * som maser på en som har søkt ferie og venter på svar, mister tilliten med
 * det samme.
 *
 * Deldagsfravær teller bare når det dekker hele dagen: har du vært borte to
 * timer, skal de resterende fortsatt føres.
 */
function isCoveredByAbsence(absences: AbsenceCover[], date: string): boolean {
  return absences.some((a) => {
    if (a.status === "rejected") return false;
    if (date < a.start_date || date > a.end_date) return false;
    if (a.hours == null) return true; // heldag
    // Deldag er registrert på start_date alene.
    return date === a.start_date && a.hours >= EXPECTED_DAILY_HOURS;
  });
}

/**
 * Arbeidsdagene som verken har en eneste timeføring eller er dekket av fravær.
 *
 * Merk at terskelen er «ingen føringer i det hele tatt», ikke «færre enn 7,5
 * timer». En underført dag er en sak for månedslukkingen (VIB-2083); en helt
 * tom dag er den som faktisk blir glemt.
 */
export function findMissingWorkdays(
  workdays: string[],
  datesWithEntries: ReadonlySet<string>,
  absences: AbsenceCover[],
): string[] {
  return workdays.filter(
    (day) => !datesWithEntries.has(day) && !isCoveredByAbsence(absences, day),
  );
}

const DAY_LABELS_NB: Record<number, string> = {
  0: "søndag",
  1: "mandag",
  2: "tirsdag",
  3: "onsdag",
  4: "torsdag",
  5: "fredag",
  6: "lørdag",
};

/** «torsdag 25.09» — samme form som cron-varselet bruker i e-posten. */
export function formatMissingDay(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${DAY_LABELS_NB[dow]} ${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")}`;
}
